import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { reviews } from '@/mocks/reviews';
import useSeo from '@/hooks/useSeo';

const typeFilters = [
  { value: 'all', label: '全部' },
  { value: 'movie', label: '影片' },
  { value: 'review', label: '影评' },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q')?.trim() || '';
  const [input, setInput] = useState(keyword);
  const [type, setType] = useState('all');

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useSeo({
    title: keyword ? `搜索「${keyword}」- 美国伦理片` : '搜索影片与影评 - 美国伦理片',
    description: keyword
      ? `在美国伦理片站内搜索「${keyword}」的结果页，包含相关影片与影评，帮助你快速定位想看的内容。`
      : '通过关键词搜索美国伦理题材电影与影评，支持按影片、影评分类过滤结果。',
    keywords: '搜索美国伦理片,伦理电影搜索,美国伦理电影检索',
  });

  const movieResults = useMemo(() => {
    if (!keyword) return [];
    const k = keyword.toLowerCase();
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(k) ||
        m.originalTitle.toLowerCase().includes(k) ||
        m.director.toLowerCase().includes(k) ||
        m.cast.join(' ').toLowerCase().includes(k) ||
        m.genres.join(' ').includes(keyword) ||
        m.tags.join(' ').includes(keyword) ||
        m.synopsis.includes(keyword),
    );
  }, [keyword]);

  const reviewResults = useMemo(() => {
    if (!keyword) return [];
    const k = keyword.toLowerCase();
    return reviews.filter(
      (r) =>
        r.title.toLowerCase().includes(k) ||
        r.movieTitle.toLowerCase().includes(k) ||
        r.excerpt.includes(keyword) ||
        r.content.includes(keyword) ||
        r.author.toLowerCase().includes(k),
    );
  }, [keyword]);

  const hotMovies = useMemo(() => [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, 4), []);

  const totalCount =
    (type === 'all' || type === 'movie' ? movieResults.length : 0) +
    (type === 'all' || type === 'review' ? reviewResults.length : 0);

  const showMovies = type === 'all' || type === 'movie';
  const showReviews = type === 'all' || type === 'review';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  const reset = () => {
    setInput('');
    setSearchParams({});
  };

  const noResult = keyword && totalCount === 0;

  return (
    <Layout>
      <section className="px-4 md:px-8 pt-8 pb-6 border-b border-background-200">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">搜索结果</span>
        </nav>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-5">站内搜索</h1>
        <form onSubmit={submit} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-2xl">
          <div className="flex items-center gap-2 flex-1 px-4 py-3 rounded-md bg-background-50 border border-background-200 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影片名称、导演或影评关键词…"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
          </div>
          <button type="submit" className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            搜索
          </button>
        </form>

        {keyword && (
          <p className="mt-4 text-sm text-foreground-500">
            当前搜索词：<span className="text-accent-700 font-semibold">「{keyword}」</span>
            <span className="mx-2 text-foreground-300">·</span>
            共找到 <span className="text-foreground-950 font-semibold">{totalCount}</span> 条结果
          </p>
        )}
      </section>

      <div className="px-4 md:px-8 py-6">
        {keyword && (
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {typeFilters.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setType(f.value)}
                className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${type === f.value ? 'bg-secondary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-700 hover:border-secondary-300'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {!keyword && (
          <div className="py-10 text-center">
            <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <p className="text-sm text-foreground-600 mb-8">输入关键词开始搜索影片或影评</p>
            <div className="text-left max-w-4xl mx-auto">
              <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500" /> 热门内容推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {hotMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </div>
        )}

        {keyword && noResult && (
          <div className="py-14 text-center">
            <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-2xl" />
            </span>
            <p className="text-sm text-foreground-600 mb-5">未找到符合条件的内容，换个关键词试试，或看看下方热门推荐</p>
            <button type="button" onClick={reset} className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap mb-10">
              重置搜索
            </button>
            <div className="text-left max-w-4xl mx-auto">
              <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500" /> 热门内容推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {hotMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </div>
        )}

        {keyword && !noResult && (
          <div className="space-y-8">
            {showMovies && movieResults.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-film-line text-primary-500" /> 影片 <span className="text-sm font-normal text-foreground-400">({movieResults.length})</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {movieResults.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}

            {showReviews && reviewResults.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-article-line text-primary-500" /> 影评 <span className="text-sm font-normal text-foreground-400">({reviewResults.length})</span>
                </h2>
                <div className="space-y-4">
                  {reviewResults.map((r) => (
                    <Link
                      key={r.id}
                      to={`/detail/${r.movieId}`}
                      className="group flex flex-col sm:flex-row gap-4 rounded-lg bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="w-full sm:w-40 h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img src={r.cover} alt={r.title} title={`${r.title} - ${r.movieTitle}`} className="w-full h-full object-top group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="inline-block px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-xs mb-2">关联影片 · {r.movieTitle}</span>
                        <h3 className="text-base font-semibold text-foreground-950 mb-1.5 group-hover:text-primary-600 transition-colors">{r.title}</h3>
                        <p className="text-sm leading-7 text-foreground-500 line-clamp-2">{r.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}